import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const AuthorityFeature = () => {
  return (
    <section id="features" aria-labelledby="authority-heading" className="py-20 md:py-28 bg-card border-y border-border">
      <div className="container mx-auto px-4">
        <div className="grid gap-14 lg:grid-cols-[1fr_1.05fr] lg:gap-20 lg:items-center">
          {/* Editorial paper panel */}
          <div className="paper-sheet rounded-sm p-7 md:p-10 relative">
            <span className="absolute inset-y-0 left-0 w-1.5 bg-gold" aria-hidden />
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-paper-foreground/55 mb-5">
              A page from the book
            </p>
            <p className="font-serif text-[15.5px] leading-[1.85] text-paper-foreground/75 mb-6">
              "Understanding your market is the foundation of every credible strategy."
            </p>
            <div className="h-px bg-paper-foreground/15 mb-6" aria-hidden />
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-foreground/60 mb-4">
              The same page, personalised for a reader
            </p>
            <p className="font-serif text-[15.5px] leading-[1.85] text-paper-foreground">
              "Based on your assessment, your strongest position is in{" "}
              <span className="font-semibold">mid-market operations</span>, where your{" "}
              <span className="font-semibold">18-month retention record</span> is your clearest
              proof. Here are the three moves to make first — and the one to stop doing."
            </p>
            <p className="mt-7 text-[13px] text-paper-foreground/60">
              Every response is grounded in your manuscript, with citations back to your own chapters.
            </p>
          </div>

          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="rule-gold" aria-hidden />
              <p className="eyebrow">Extend</p>
            </div>
            <h2 id="authority-heading" className="text-3xl sm:text-4xl md:text-5xl font-semibold leading-[1.1] mb-6">
              A book that reads the reader back.
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              A printed book gives everyone the same experience. Extend gives every
              reader their own: a diagnostic, a personalised report, and a clear next step that leads
              straight into your coaching, consulting or advisory work.
            </p>

            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6 mb-10">
              {[
                { t: "Interactive assessments", d: "Readers self-diagnose using your frameworks and receive a tailored report." },
                { t: "AI trained on your words", d: "Answers cite your chapters, so guidance always sounds like you." },
                { t: "A qualified pipeline", d: "Every completed assessment tells you who is ready to talk, and why." },
                { t: "Revenue after launch", d: "Subscriptions, programmes and upgrades that keep earning past launch week." },
              ].map((item) => (
                <div key={item.t}>
                  <h3 className="text-base font-semibold mb-1.5">{item.t}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.d}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild className="bg-gold text-gold-foreground hover:bg-gold-light font-semibold">
                <Link to="/extend" className="inline-flex items-center gap-2">
                  Explore Extend
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/demo">See a live example</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AuthorityFeature;
